/**
 * Le fil, groupé : des jours, et dans chaque jour des salves.
 *
 * Une SALVE est une suite de messages du même auteur, rapprochés dans le temps : ses bulles se
 * serrent, la dernière porte la queue et l'heure. Un JOUR ouvre un séparateur daté.
 *
 * C'est une dérivation pure sur les formes du fil (`ui/types/studentConversation`), lue par le
 * view-model de la conversation avec un tuteur et, dans le design system, par les fils qui se
 * groupent sans view-model (l'erreur d'un checkpoint) et par les mocks : d'où sa place ici, la seule
 * couche que les deux lisent. Le fil reçoit ses jours tout faits — il ne groupe rien lui-même.
 *
 * La clé d'un jour et celle d'une salve prennent l'id de leur PREMIER message : charger des messages
 * plus anciens du même jour changerait ces clés. Le view-model de la conversation les rend stables
 * (`stableConversationKeys`).
 */
import { type UIConversationDay, type UIConversationMessage } from "../types/studentConversation"
import { formatDayLabel, formatTime } from "./dateFormatter"

/** Au-delà de ce silence, le même auteur ouvre une nouvelle salve. */
const BURST_GAP_MS = 5 * 60 * 1000

export type GroupConversationOptions = {
    /**
     * Le fil montre ses jours et ses heures — vrai par défaut. La conversation avec Sam n'en
     * montre aucun aujourd'hui : elle passe `false`, et ses salves ne dépendent que de l'auteur.
     */
    dated?: boolean
}

export function groupConversation(
    messages: UIConversationMessage[],
    options: GroupConversationOptions = {}
): UIConversationDay[] {
    const { dated = true } = options
    const days: UIConversationDay[] = []

    let previous: UIConversationMessage | null = null
    for (const message of messages) {
        const label = dated && message.sentAt != null ? formatDayLabel(message.sentAt, true, true) : undefined
        let day = days[days.length - 1]
        // Un message sans date dans un fil daté (un envoi en cours) n'ouvre pas de jour : il reste
        // dans le jour en cours, qui ne se rouvre donc pas après lui.
        const undated = dated && message.sentAt == null
        if (day == null || (!undated && day.label !== label)) {
            day = { id: `day-${message.id}`, label, bursts: [] }
            days.push(day)
            previous = null
        }

        let burst = day.bursts[day.bursts.length - 1]
        if (burst == null || previous == null || !continuesBurst(previous, message, dated)) {
            burst = { id: `burst-${message.id}`, author: message.author, messages: [] }
            day.bursts.push(burst)
        }
        burst.messages.push(message)
        if (dated && message.sentAt != null) burst.timeLabel = formatTime(message.sentAt)

        previous = message
    }

    return days
}

/** En cours d'envoi ou en échec : le message porte sa propre ligne d'état. */
function isPending(message: UIConversationMessage): boolean {
    return message.status === "sending" || message.status === "failed"
}

function continuesBurst(previous: UIConversationMessage, next: UIConversationMessage, dated: boolean): boolean {
    if (previous.author !== next.author) return false
    // Dans un fil à plusieurs, deux élèves qui s'enchaînent ne se lisent pas comme une seule salve.
    if (previous.sender?.id !== next.sender?.id) return false
    // Un message qui n'est pas parti ne se range pas dans la salve des messages partis : celle-ci
    // se ferme sur son heure, et lui se lit seul, avec « Envoi... » ou « Non envoyé ».
    if (isPending(previous) || isPending(next)) return false
    if (!dated || previous.sentAt == null || next.sentAt == null) return true
    return Date.parse(next.sentAt) - Date.parse(previous.sentAt) <= BURST_GAP_MS
}
